/**
 * Keyboard Handler Utility
 * Manages iOS and Android keyboard behavior in PWA mode
 */

class KeyboardHandler {
  constructor() {
    this.isKeyboardVisible = false;
    this.originalScrollY = 0;
    this.focusedElement = null;
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    this.isAndroid = /Android/i.test(navigator.userAgent);
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;

    // Only initialize if in standalone mode
    if (this.isStandalone) {
      this.init();
    }
  }

  init() {
    // Visual Viewport API for better keyboard detection
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleViewportResize.bind(this));
      window.visualViewport.addEventListener('scroll', this.handleViewportScroll.bind(this));
    }

    // Input focus/blur events
    document.addEventListener('focusin', this.handleFocusIn.bind(this));
    document.addEventListener('focusout', this.handleFocusOut.bind(this));

    // Prevent background scroll on iOS when input is focused
    if (this.isIOS) {
      document.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    }
  }

  handleViewportResize() {
    if (!window.visualViewport) return;

    const viewport = window.visualViewport;
    const windowHeight = window.innerHeight;
    const viewportHeight = viewport.height;
    const heightDiff = windowHeight - viewportHeight;

    // Keyboard is visible if viewport height is significantly smaller
    const wasKeyboardVisible = this.isKeyboardVisible;
    this.isKeyboardVisible = heightDiff > 150; // Threshold for keyboard detection

    if (this.isKeyboardVisible !== wasKeyboardVisible) {
      if (this.isKeyboardVisible) {
        this.onKeyboardShow(heightDiff);
      } else {
        this.onKeyboardHide();
      }
    }
  }

  handleViewportScroll() {
    if (!window.visualViewport || !this.isKeyboardVisible) return;

    // Ensure focused input stays visible
    if (this.focusedElement) {
      const rect = this.focusedElement.getBoundingClientRect();
      const viewportTop = window.visualViewport.offsetTop;
      const viewportBottom = viewportTop + window.visualViewport.height;

      // If input is outside visible viewport, scroll it into view
      if (rect.top < viewportTop || rect.bottom > viewportBottom) {
        this.focusedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  handleFocusIn(event) {
    const target = event.target;
    
    // Only handle input elements
    if (!this.isInputElement(target)) return;

    this.focusedElement = target;
    this.originalScrollY = window.scrollY;

    // Add body class for CSS targeting
    document.body.classList.add('input-focused');

    // iOS specific: prevent background scroll
    if (this.isIOS) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.originalScrollY}px`;
      document.body.style.width = '100%';
    }

    // Scroll input into view with some padding
    setTimeout(() => {
      target.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }, 300); // Delay for keyboard animation
  }

  handleFocusOut(event) {
    const target = event.target;
    
    if (!this.isInputElement(target)) return;

    this.focusedElement = null;

    // Remove body class
    document.body.classList.remove('input-focused');

    // iOS specific: restore scroll position
    if (this.isIOS) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      // Restore scroll position
      window.scrollTo(0, this.originalScrollY);
    }
  }

  handleTouchMove(event) {
    // Prevent scroll when keyboard is visible and input is focused on iOS
    if (this.isKeyboardVisible && this.focusedElement && this.isIOS) {
      const target = event.target;
      
      // Allow scroll only within the focused input or scrollable parents
      if (!target.closest('input, textarea, select, [contenteditable]') && 
          !target.closest('.scrollable')) {
        event.preventDefault();
      }
    }
  }

  onKeyboardShow(keyboardHeight) {
    document.body.classList.add('keyboard-visible');
    
    // Dispatch custom event for components to react
    window.dispatchEvent(new CustomEvent('keyboard-show', { 
      detail: { height: keyboardHeight } 
    }));

    // Create or update keyboard spacer
    let spacer = document.querySelector('.keyboard-spacer');
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.className = 'keyboard-spacer';
      document.body.appendChild(spacer);
    }
    spacer.style.height = `${keyboardHeight}px`;
  }

  onKeyboardHide() {
    document.body.classList.remove('keyboard-visible');
    
    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('keyboard-hide'));

    // Remove keyboard spacer
    const spacer = document.querySelector('.keyboard-spacer');
    if (spacer) {
      spacer.style.height = '0';
    }
  }

  isInputElement(element) {
    const tagName = element.tagName.toLowerCase();
    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      element.contentEditable === 'true'
    );
  }

  destroy() {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportResize);
      window.visualViewport.removeEventListener('scroll', this.handleViewportScroll);
    }
    
    document.removeEventListener('focusin', this.handleFocusIn);
    document.removeEventListener('focusout', this.handleFocusOut);
    
    if (this.isIOS) {
      document.removeEventListener('touchmove', this.handleTouchMove);
    }

    // Cleanup spacer
    const spacer = document.querySelector('.keyboard-spacer');
    if (spacer) {
      spacer.remove();
    }
  }
}

// Create singleton instance
let keyboardHandlerInstance = null;

export const initKeyboardHandler = () => {
  if (!keyboardHandlerInstance) {
    keyboardHandlerInstance = new KeyboardHandler();
  }
  return keyboardHandlerInstance;
};

export const destroyKeyboardHandler = () => {
  if (keyboardHandlerInstance) {
    keyboardHandlerInstance.destroy();
    keyboardHandlerInstance = null;
  }
};

const keyboardHandler = {
  init: initKeyboardHandler,
  destroy: destroyKeyboardHandler
};

export default keyboardHandler;
